import FormQuestion from "./model/FormQuestion";
import {useEffect, useRef, useState} from "react";

/**
 * Props for this component
 */
interface CheckboxMultiSelectProps {
  question: FormQuestion;
  listOfOptions: FormQuestion[] | undefined;
  locked: boolean;
  responseMap?: Map<number, string>;
  mandatory?: boolean;
}

export default function CheckboxMultiSelect({
                                              question,
                                              listOfOptions,
                                              locked,
                                              responseMap,
                                              mandatory = false
                                            }: CheckboxMultiSelectProps) {
  // when first loading this component, get the current count of checkboxes which have already been selected, otherwise set selected count to 0
  const [selectedCount, setSelectedCount] = useState<number>(listOfOptions?.filter(item => item.response === "TRUE").length ?? 0);

  useEffect(() => {
    if (!mandatory) {
      // if we are no longer showing the subquestions, we should have 0 selected checkboxes
      setSelectedCount(0);
      return;
    }

    // get reference to checkbox container to add styling
    const container = validationRef.current
    ?.closest(`#checkboxMultiSelect${question.index}CheckboxArea`);

    // add custom validation classes to overwrite default bootstrap validation styling
    if (selectedCount <= 0) {
      validationRef.current?.setCustomValidity("Error");

      container?.classList.add("is-invalid");
      container?.classList.remove("is-valid");
    } else {
      validationRef.current?.setCustomValidity("");

      container?.classList.remove("is-invalid");
      container?.classList.add("is-valid");
    }

  }, [selectedCount, mandatory]);

  // create a ref to a hidden input so we can handle validation independently to the rest of the form
  const validationRef = useRef<HTMLInputElement>(null);

  /**
   * This function will add the response for this question to a map linking the response to its respective question
   * ID. In the case of a new response, it will save the value regardless, but if we are editing an existing response
   * we will only have the value in the map if it is different from the initial response.
   *
   * @param optionId The ID of the selected option
   * @param newResponse The value we wish to save
   */
  function updateResponseMap(option: FormQuestion, newResponse: string) {
    if (!responseMap) {
      return; // This component is not required to save responses
    }

    if (option.response && responseMap.has(option.id)) {
      // This is to only add the answer change to the map when we edit an existing answer
      responseMap.delete(option.id);
    } else {
      // In case it is a new form answer we add it regardless
      responseMap.set(option.id, newResponse)
    }

    if (mandatory) {
      // update the count of selected checkboxes for validation purposes
      setSelectedCount(prev => newResponse == "TRUE" ? prev + 1 : prev - 1);
    }
  }

  return <div id={`checkboxMultiSelect${question.index}`}>
    {question.text ? (
            <label id={`checkboxMultiSelect${question.index}Label`} className="question-text">{question.text}</label>)
        :
        (<></>)}
    <div id={`checkboxMultiSelect${question.index}CheckboxArea`}
         className={`checkbox-area ${mandatory ? ' mandatory' : ''}`}>
      {mandatory && <input hidden ref={validationRef}/>}
      {listOfOptions?.map((option, index) => (
              <div key={option.id} id={`checkboxMultiSelect${option.id}`} className="form-check">
                <input id={`checkboxMultiSelect${option.id}Check`}
                       className="form-check-input"
                       type="checkbox"
                       onChange={(e) =>
                           (updateResponseMap(option, e.currentTarget.checked ? "TRUE" : "FALSE"))}
                       disabled={locked}
                       defaultChecked={option.response !== null && option.response === "TRUE"}
                />
                <label id={`checkboxMultiSelect${option.id}Label`}
                       className="form-check-label"
                       htmlFor="checkboxMultiSelectCheck">
                  {option.text}
                </label>
              </div>
          )
      )}
      <div id="checkboxMultiSelectAreaFeedback" className="invalid-feedback">
        Please select at least one option.
      </div>
    </div>
  </div>
}